/**
 * Jurídico: importação de obras em lote (propriedade intelectual, ADR 0065). Modelo de planilha
 * próprio: colunas pelo nome (em português), valores aceitos pelo rótulo da tela ou pelo código.
 * Nada é presumido: titularidade e proteção vazias entram como "em análise" e "sem registro",
 * como no cadastro pela tela.
 */
import {
  IP_WORK_AUTHOR_ROLES,
  IP_WORK_CATEGORIES,
  IP_WORK_ORIGINS,
  IP_WORK_OWNERSHIP,
  IP_WORK_PROTECTION,
} from './legal-ip-works.ts';
import { looseText } from './legal-contracts.ts';

/** Colunas do modelo oficial; só "Empresa", "Título" e "Categoria" são obrigatórias. */
export const WORK_SHEET_COLUMNS = {
  entity: 'Empresa',
  title: 'Título',
  category: 'Categoria',
  universe: 'Universo ou coleção',
  createdOn: 'Data de criação',
  description: 'Descrição',
  ownershipStatus: 'Titularidade',
  ownershipOrigin: 'Origem da titularidade',
  holderName: 'Detentor dos direitos',
  protectionStatus: 'Proteção',
  registrationBody: 'Órgão de registro',
  registrationNumber: 'Número do registro',
  registrationDate: 'Data do registro',
  authors: 'Autores',
} as const;
export type WorkSheetField = keyof typeof WORK_SHEET_COLUMNS;
export const WORK_SHEET_REQUIRED: WorkSheetField[] = ['entity', 'title', 'category'];

/** Posição de cada coluna pelo nome do cabeçalho (sem acento nem caixa). */
export function workSheetColumns(header: (string | null)[]) {
  const index = new Map(header.map((h, i) => [looseText(String(h ?? '')), i]));
  const map: Partial<Record<WorkSheetField, number>> = {};
  for (const [field, name] of Object.entries(WORK_SHEET_COLUMNS)) {
    const i = index.get(looseText(name));
    if (i !== undefined) map[field as WorkSheetField] = i;
  }
  const missing = WORK_SHEET_REQUIRED.filter((f) => map[f] === undefined).map(
    (f) => WORK_SHEET_COLUMNS[f],
  );
  return { map, missing };
}

const LABELS: Record<string, Record<string, string>> = {
  category: {
    'escultura fisica': 'sculpture_physical',
    'escultura digital': 'sculpture_digital',
    'modelo 3d': 'model_3d',
    'arquivo stl': 'stl_file',
    stl: 'stl_file',
    'ilustracao 2d': 'illustration_2d',
    ilustracao: 'illustration_2d',
    personagem: 'character',
    'concept art': 'concept_art',
    'arte conceitual': 'concept_art',
    'ativo de jogo': 'game_asset',
    'ativo de produto': 'product_asset',
    outra: 'other',
    outro: 'other',
  },
  ownershipStatus: {
    'em analise': 'under_review',
    'da loot': 'owned',
    propria: 'owned',
    'cedida a loot': 'assigned_to_loot',
    'licenciada a loot': 'licensed_to_loot',
    'de terceiro': 'third_party',
    terceiro: 'third_party',
  },
  ownershipOrigin: {
    'criacao de colaborador': 'employee_creation',
    'encomenda com cessao': 'commissioned_assignment',
    licenca: 'license',
    aquisicao: 'acquisition',
    terceiro: 'third_party',
    'nao sabido': 'unknown',
    desconhecida: 'unknown',
  },
  protectionStatus: {
    'sem registro': 'unregistered',
    'registro em andamento': 'registration_pending',
    registrada: 'registered',
    registrado: 'registered',
  },
  role: {
    autor: 'author',
    autora: 'author',
    coautor: 'coauthor',
    coautora: 'coauthor',
    ilustrador: 'illustrator',
    ilustradora: 'illustrator',
    escultor: 'sculptor',
    escultora: 'sculptor',
    modelador: 'modeler',
    modeladora: 'modeler',
    designer: 'designer',
    outro: 'other',
    outra: 'other',
  },
};
const CODES: Record<string, readonly string[]> = {
  category: IP_WORK_CATEGORIES,
  ownershipStatus: IP_WORK_OWNERSHIP,
  ownershipOrigin: IP_WORK_ORIGINS,
  protectionStatus: IP_WORK_PROTECTION,
  role: IP_WORK_AUTHOR_ROLES,
};

/** Valor da lista pelo rótulo em português ou pelo código; null quando não reconhecido. */
export function workSheetChoice(
  kind: 'category' | 'ownershipStatus' | 'ownershipOrigin' | 'protectionStatus' | 'role',
  value: string,
): string | null {
  const v = looseText(value);
  if (!v) return null;
  const code = CODES[kind]!.find((c) => looseText(c) === v || c === value.trim());
  return code ?? LABELS[kind]![v] ?? null;
}

/**
 * Autores na célula: "Nome (função); Outro nome". Sem função, "autor". Função não reconhecida
 * entra como "outro" e o texto vai na observação.
 */
export function workSheetAuthors(
  value: string,
): { name: string; role: string; note: string | null }[] {
  return value
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .map((part) => {
      const m = /^(.*?)\s*\(([^)]*)\)\s*$/.exec(part);
      const name = (m ? m[1]! : part).trim().slice(0, 300);
      const roleText = m ? m[2]!.trim() : '';
      const role = roleText ? workSheetChoice('role', roleText) : 'author';
      return { name, role: role ?? 'other', note: role ? null : roleText.slice(0, 1000) };
    })
    .filter((a) => a.name !== '');
}

/** Chave de obra repetida: mesma empresa, mesmo título e mesmo universo (sem acento nem caixa). */
export function workKey(entityId: string, title: string, universe: string | null) {
  return `${entityId}|${looseText(title)}|${looseText(universe ?? '')}`;
}
